import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle, Clock, X,
  Handshake, Upload, FileText, User, ShieldCheck, Building2,
  MapPin, Briefcase, FileSignature, AlertTriangle,
  LogIn, LayoutDashboard, Home, RefreshCw
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { ar } from "date-fns/locale";
import { AgreementStep, AgreementResult } from "@/components/affiliate/agreement/AgreementStep";
import { captureAgreementToPdf } from "@/components/affiliate/agreement/captureAgreementPdf";
import { COUNTRIES } from "@/lib/countries";

const MAX_PDF_SIZE = 10 * 1024 * 1024;

const STEPS = [
  { icon: User, titleEn: "Getting Started", titleAr: "البداية", descEn: "Tell us about yourself", descAr: "أخبرنا عن نفسك" },
  { icon: ShieldCheck, titleEn: "Verification", titleAr: "التحقق", descEn: "Identity & registration details", descAr: "بيانات الهوية والتسجيل" },
  { icon: Building2, titleEn: "Banking Details", titleAr: "البيانات البنكية", descEn: "Your payment information", descAr: "معلومات الدفع الخاصة بك" },
  { icon: MapPin, titleEn: "National Address", titleAr: "العنوان الوطني", descEn: "Your official address", descAr: "عنوانك الرسمي" },
  { icon: Briefcase, titleEn: "Experience", titleAr: "الخبرة", descEn: "Tell us about your background", descAr: "أخبرنا عن خلفيتك" },
  { icon: FileSignature, titleEn: "Agreement", titleAr: "الاتفاقية", descEn: "Review & sign", descAr: "مراجعة وتوقيع" },
];

const LAST_STEP = 5;

const AffiliateSignUp = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [existingApplication, setExistingApplication] = useState<any>(null);
  const [reapplyingFromId, setReapplyingFromId] = useState<string | null>(null);
  const [animDir, setAnimDir] = useState<'forward' | 'back'>('forward');

  const [companyRegFile, setCompanyRegFile] = useState<File | null>(null);
  const [vatRegFile, setVatRegFile] = useState<File | null>(null);
  const [nationalIdFile, setNationalIdFile] = useState<File | null>(null);
  const [agreement, setAgreement] = useState<AgreementResult>({ accepted: false, signature: null, stamp: null, acceptedAt: null });
  
  const [dobParts, setDobParts] = useState<{ d?: number; m?: number; y?: number }>({});

  const [formData, setFormData] = useState({
    applicantName: "",
    companyName: "",
    applicantType: "individual",
    email: user?.email || "",
    phone: "",
    experience: "",
    reason: "",
    websiteUrl: "",
    personInChargeName: "",
    personInChargeTitle: "",
    crNumber: "",
    vatNumber: "",
    country: "SA",
    nationalIdNumber: "",
    dateOfBirth: "",
    bankName: "",
    iban: "",
    accountHolderName: "",
    city: "",
    district: "",
    street: "",
    postalCode: "",
    buildingNumber: "",
    additionalNumber: "",
  });

  const t = (en: string, ar: string) => isRTL ? ar : en;
  const isAgency = formData.applicantType === 'agency';

  useEffect(() => {
    const checkExisting = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('affiliate_applications')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        if (error) throw error;
        const ACTIVE_STATUSES = ['pending', 'approved', 'rejected'];
        if (data && ACTIVE_STATUSES.includes(data.approval_status)) {
          setExistingApplication(data);
        } else if (data) {
          // Non-active (e.g. revoked) — allow re-apply, overwrite the existing row.
          setReapplyingFromId(data.id);
        }
      } catch (error) {
        console.error('Error checking existing application:', error);
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExisting();
  }, [user]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (f: File | null) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast({ title: t("Invalid file", "ملف غير صالح"), description: t("Please upload a PDF file", "يرجى رفع ملف PDF"), variant: "destructive" });
      e.target.value = '';
      return;
    }
    if (file.size > MAX_PDF_SIZE) {
      toast({ title: t("File too large", "ملف كبير جداً"), description: t("Maximum file size is 10MB", "الحد الأقصى 10 ميجابايت"), variant: "destructive" });
      e.target.value = '';
      return;
    }
    setter(file);
  };

  const uploadFile = async (file: File, folder: string): Promise<string> => {
    const ext = file.name.split('.').pop();
    const path = `${user!.id}/${folder}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('affiliate-documents').upload(path, file);
    if (error) throw error;
    return path;
  };

  const update = (field: string, value: string) => setFormData(prev => ({ ...prev, [field]: value }));

  // Per-step validation
  const validateStep = (s: number): string | null => {
    switch (s) {
      case 0:
        if (!formData.applicantName) return t("Full name is required", "الاسم الكامل مطلوب");
        if (!formData.email) return t("Email is required", "البريد الإلكتروني مطلوب");
        if (!formData.phone) return t("Phone is required", "رقم الهاتف مطلوب");
        if (isAgency && !formData.companyName) return t("Company name is required", "اسم الشركة مطلوب");
        return null;
      case 1:
        if (isAgency) {
          if (!formData.personInChargeName || !formData.personInChargeTitle) return t("Person in charge details are required", "بيانات المسؤول مطلوبة");
          if (!formData.crNumber) return t("CR number is required", "رقم السجل التجاري مطلوب");
          if (!formData.vatNumber) return t("VAT number is required", "الرقم الضريبي مطلوب");
          if (!companyRegFile) return t("Company registration document is required", "وثيقة السجل التجاري مطلوبة");
          if (!vatRegFile) return t("VAT registration document is required", "وثيقة التسجيل الضريبي مطلوبة");
        } else {
          if (!formData.country) return t("Country is required", "الدولة مطلوبة");
          if (!formData.nationalIdNumber) return formData.country === "SA"
            ? t("National ID or Iqama is required", "الهوية الوطنية أو الإقامة مطلوبة")
            : t("National ID number is required", "رقم الهوية الوطنية مطلوب");
          if (!formData.dateOfBirth) return t("Date of birth is required", "تاريخ الميلاد مطلوب");
          if (!nationalIdFile) return formData.country === "SA"
            ? t("National ID or Iqama document is required", "وثيقة الهوية الوطنية أو الإقامة مطلوبة")
            : t("National ID document is required", "وثيقة الهوية الوطنية مطلوبة");
        }
        return null;
      case 2:
        if (!formData.bankName || !formData.iban || !formData.accountHolderName)
          return t("All bank information fields are required", "جميع حقول البنك مطلوبة");
        return null;
      case 3:
        if (!formData.city || !formData.district || !formData.street || !formData.postalCode || !formData.buildingNumber || !formData.additionalNumber)
          return t("All national address fields are required", "جميع حقول العنوان الوطني مطلوبة");
        return null;
      case 5:
        if (!agreement.accepted) return t("You must accept the agreement", "يجب الموافقة على الاتفاقية");
        if (isAgency) {
          if (!agreement.signature) return t("Authorized signature is required", "التوقيع المعتمد مطلوب");
          if (!agreement.stamp) return t("Company stamp is required", "ختم الشركة مطلوب");
        }
        return null;
      default:
        return null;
    }
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      toast({ title: t("Missing Information", "معلومات ناقصة"), description: err, variant: "destructive" });
      return;
    }
    setAnimDir('forward');
    setStep(s => Math.min(s + 1, LAST_STEP));
  };

  const goBack = () => {
    setAnimDir('back');
    setStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (!user) { navigate('/auth'); return; }
    setLoading(true);
    try {
      const documentUrls: Record<string, string> = {};
      if (isAgency) {
        documentUrls.company_registration_url = await uploadFile(companyRegFile!, 'company_registration');
        documentUrls.vat_registration_url = await uploadFile(vatRegFile!, 'vat_registration');
      } else {
        documentUrls.national_id_url = await uploadFile(nationalIdFile!, 'national_id');
      }

      // Upload signature & stamp for agencies
      let signatureUrl: string | null = null;
      let stampUrl: string | null = null;
      const dataUrlToBlob = (d: string) => fetch(d).then(r => r.blob());
      if (isAgency && agreement.signature) {
        const blob = await dataUrlToBlob(agreement.signature);
        const path = `${user.id}/agreement_signature_${Date.now()}.png`;
        const { error: sErr } = await supabase.storage.from('affiliate-documents').upload(path, blob, { contentType: 'image/png' });
        if (sErr) throw sErr;
        signatureUrl = path;
      }
      if (isAgency && agreement.stamp) {
        const blob = await dataUrlToBlob(agreement.stamp);
        const path = `${user.id}/agreement_stamp_${Date.now()}.png`;
        const { error: stErr } = await supabase.storage.from('affiliate-documents').upload(path, blob, { contentType: 'image/png' });
        if (stErr) throw stErr;
        stampUrl = path;
      }

      // Agreement PDF is now generated server-side after the application row is inserted (see below).

      const payload = {
          user_id: user.id,
          applicant_name: formData.applicantName,
          company_name: formData.companyName || null,
          applicant_type: formData.applicantType,
          email: formData.email,
          phone: formData.phone || null,
          experience: formData.experience || null,
          reason: formData.reason || null,
          commission_preference: 'percentage',
          website_url: formData.websiteUrl || null,
          person_in_charge_name: isAgency ? formData.personInChargeName : formData.applicantName,
          person_in_charge_title: isAgency ? formData.personInChargeTitle : null,
          cr_number: isAgency ? formData.crNumber : null,
          vat_number: isAgency ? formData.vatNumber : null,
          national_id_number: !isAgency ? formData.nationalIdNumber : null,
          country: !isAgency ? formData.country : null,
          date_of_birth: !isAgency ? formData.dateOfBirth : null,
          bank_info: {
            bank_name: formData.bankName,
            iban: formData.iban,
            account_holder_name: formData.accountHolderName,
          },
          national_address: {
            city: formData.city,
            district: formData.district,
            street: formData.street,
            postal_code: formData.postalCode,
            building_number: formData.buildingNumber,
            additional_number: formData.additionalNumber,
          },
          document_urls: documentUrls,
          agreement_accepted_at: (agreement.acceptedAt ?? new Date()).toISOString(),
          agreement_signature_url: signatureUrl,
          agreement_stamp_url: stampUrl,
        };

      let inserted: { id: string } | null = null;
      if (reapplyingFromId) {
        const { data: updated, error } = await supabase
          .from('affiliate_applications')
          .update({
            ...payload,
            approval_status: 'pending',
            rejection_reason: null,
            approved_at: null,
            approved_by: null,
            agreement_pdf_url: null,
          } as any)
          .eq('id', reapplyingFromId)
          .select('id')
          .single();
        if (error) throw error;
        inserted = updated;
      } else {
        const { data: created, error } = await supabase
          .from('affiliate_applications')
          .insert(payload as any)
          .select('id')
          .single();
        if (error) throw error;
        inserted = created;
      }

      // Generate the signed agreement PDF from the same React components shown
      // to the user — payload-based so it cannot drift from what was signed.
      if (inserted?.id) {
        try {
          const pdfBlob = await captureAgreementToPdf({
            isAgency,
            lang: isRTL ? 'ar' : 'en',
            data: {
              applicantName: formData.applicantName,
              companyName: formData.companyName,
              crNumber: formData.crNumber,
              vatNumber: formData.vatNumber,
              email: formData.email,
              personInChargeName: formData.personInChargeName,
              personInChargeTitle: formData.personInChargeTitle,
              nationalIdNumber: formData.nationalIdNumber,
              city: formData.city,
            },
            signature: agreement.signature,
            stamp: agreement.stamp,
            acceptedAt: agreement.acceptedAt ?? new Date(),
            documentDate: agreement.acceptedAt ?? new Date(),
          });

          const pdfPath = `${user.id}/agreement_${Date.now()}.pdf`;
          const { error: pdfUpErr } = await supabase.storage
            .from('affiliate-documents')
            .upload(pdfPath, pdfBlob, { contentType: 'application/pdf', upsert: false });
          if (pdfUpErr) throw pdfUpErr;

          const { error: pdfPatchErr } = await supabase
            .from('affiliate_applications')
            .update({ agreement_pdf_url: pdfPath } as any)
            .eq('id', inserted.id);
          if (pdfPatchErr) throw pdfPatchErr;
        } catch (pdfErr) {
          console.error('Agreement PDF generation failed (non-fatal):', pdfErr);
          toast({
            title: t("Agreement saved", "تم حفظ الاتفاقية"),
            description: t(
              "A copy of the signed agreement could not be generated. An admin can verify your signature directly.",
              "تعذر إنشاء نسخة من الاتفاقية الموقعة. يمكن للمسؤول التحقق من توقيعك مباشرة."
            ),
          });
        }
      }

      // Send confirmation email (with signed agreement attached for agencies). Non-blocking.
      try {
        await supabase.functions.invoke('send-affiliate-application-email', {
          body: { applicationId: inserted.id },
        });
      } catch (emailErr) {
        console.error('Failed to send affiliate application confirmation email (non-fatal):', emailErr);
      }

      toast({
        title: t("Application Submitted!", "تم إرسال الطلب!"),
        description: t("Your referral partner application is under review. We'll notify you soon.", "طلب الشراكة قيد المراجعة. سنبلغك قريباً."),
      });
      navigate('/');
    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast({ title: t("Error", "خطأ"), description: error.message || t("An error occurred", "حدث خطأ"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // ---------- Early returns ----------
  if (!user) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center bg-muted/30 p-4", isRTL ? 'text-right' : 'text-left')} dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="max-w-md w-full border border-border/60 shadow-sm rounded-xl">
          <CardContent className="pt-8 pb-8 text-center space-y-5">
            <div className="mx-auto h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center">
              <Handshake className="h-7 w-7 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">{t("You must be logged in to apply", "يجب تسجيل الدخول للتقديم")}</p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate('/auth')}
                className="w-full h-11 rounded-lg gap-2 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <LogIn className="h-4 w-4" />
                {t("Login", "تسجيل الدخول")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="w-full h-11 rounded-lg gap-2 font-medium text-muted-foreground hover:text-foreground"
              >
                <Home className="h-4 w-4" />
                {t("Back to Home", "العودة للرئيسية")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingExisting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (existingApplication) {
    const statusConfig = {
      approved: { icon: CheckCircle, color: 'text-green-600', bgIcon: 'bg-green-500/10', borderTop: 'border-t-green-500' },
      pending: { icon: Clock, color: 'text-yellow-600', bgIcon: 'bg-yellow-500/10', borderTop: 'border-t-yellow-500' },
      rejected: { icon: X, color: 'text-red-600', bgIcon: 'bg-red-500/10', borderTop: 'border-t-red-500' },
    };
    const sc = statusConfig[existingApplication.approval_status as keyof typeof statusConfig] || statusConfig.pending;
    const StatusIcon = sc.icon;

    return (
      <div className={cn("min-h-screen flex items-center justify-center bg-muted/30 p-4", isRTL ? 'text-right' : 'text-left')} dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className={cn("max-w-md w-full border border-border/60 shadow-sm rounded-xl border-t-2", sc.borderTop)}>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className={cn("h-14 w-14 rounded-xl flex items-center justify-center", sc.bgIcon)}>
                <StatusIcon className={cn("h-7 w-7", sc.color)} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t("Application Status", "حالة الطلب")}</h2>
                <p className="text-sm text-muted-foreground">{t("Your referral partner application", "طلب شراكة الإحالة الخاص بك")}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <div className="h-4 w-1 rounded-full bg-primary" />
              <h3 className="text-sm font-semibold text-foreground">{t("Status", "الحالة")}</h3>
            </div>
            <div className="flex items-center justify-between -mt-2">
              <span className="text-muted-foreground text-sm">{t("Current state", "الحالة الحالية")}</span>
              <Badge variant={
                existingApplication.approval_status === 'approved' ? 'default' :
                existingApplication.approval_status === 'pending' ? 'secondary' : 'destructive'
              }>
                {existingApplication.approval_status === 'approved'
                  ? t("Approved", "تمت الموافقة")
                  : existingApplication.approval_status === 'pending'
                  ? t("Pending Review", "قيد المراجعة")
                  : t("Rejected", "مرفوض")}
              </Badge>
            </div>

            {existingApplication.rejection_reason && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div><strong>{t("Reason:", "السبب:")}</strong> {existingApplication.rejection_reason}</div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {existingApplication.approval_status === 'approved' && (
                <Button
                  onClick={() => navigate('/affiliate-dashboard')}
                  className="group w-full h-11 rounded-lg gap-2 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  {t("Go to Dashboard", "الذهاب للوحة التحكم")}
                  <ArrowRight className={cn("h-4 w-4 transition-transform group-hover:translate-x-0.5", isRTL && "rotate-180 group-hover:-translate-x-0.5")} />
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full h-11 rounded-lg gap-2 font-medium border-border/70 hover:border-foreground/30 hover:bg-muted/60"
              >
                <Home className="h-4 w-4" />
                {t("Back to Home", "العودة للرئيسية")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- File upload component ----------
  const FileUploadField = ({ label, file, onChange }: { label: string; file: File | null; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      {file ? (
        <label className="flex items-center gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors">
          <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <FileText className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
            <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
          </div>
          <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium text-primary hover:bg-primary/10 transition-colors">
            <RefreshCw className="h-3.5 w-3.5" />
            {t("Replace", "استبدال")}
          </span>
          <input type="file" accept="application/pdf" className="hidden" onChange={onChange} />
        </label>
      ) : (
        <label className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border border-dashed border-border cursor-pointer hover:border-primary/40 hover:bg-muted/30 transition-colors">
          <Upload className="h-6 w-6 text-muted-foreground/60" />
          <span className="text-xs text-muted-foreground">{t("PDF, max 10MB", "PDF، بحد أقصى 10 ميجا")}</span>
          <span className="inline-flex items-center justify-center gap-2 h-8 px-3 rounded-md border border-border/70 bg-background text-xs font-medium text-foreground hover:bg-muted/60 transition-colors">
            <Upload className="h-3.5 w-3.5" />
            {t("Choose file", "اختر ملف")}
          </span>
          <input type="file" accept="application/pdf" className="hidden" onChange={onChange} />
        </label>
      )}
    </div>
  );

  // ---------- Step progress (segmented bar) ----------
  const StepProgress = () => {
    const totalSteps = STEPS.length;
    const progress = ((step + 1) / totalSteps) * 100;
    const currentTitle = isRTL ? STEPS[step].titleAr : STEPS[step].titleEn;
    return (
      <div className="mb-6 space-y-2">
        <div className="hidden sm:flex items-center justify-between gap-1">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-2 w-2 rounded-full transition-colors duration-300",
                i <= step ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("Step", "خطوة")} {step + 1} {t("of", "من")} {totalSteps}
          </span>
          <span className="text-sm font-semibold text-foreground truncate">{currentTitle}</span>
        </div>
      </div>
    );
  };

  // ---------- Step header (inline) ----------
  const StepHeader = () => {
    const Icon = STEPS[step].icon;
    return (
      <div className="border-b border-border/60 pb-4 mb-5">
        <div className="flex items-center gap-2">
          <Icon className="h-[18px] w-[18px] text-primary shrink-0" />
          <h2 className="text-lg font-semibold text-foreground">{isRTL ? STEPS[step].titleAr : STEPS[step].titleEn}</h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{isRTL ? STEPS[step].descAr : STEPS[step].descEn}</p>
      </div>
    );
  };

  // ---------- Section subheader ----------
  const SectionHeader = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-2 pt-2">
      <div className="h-4 w-1 rounded-full bg-primary" />
      <h3 className="text-sm font-semibold text-foreground">{children}</h3>
    </div>
  );

  // ---------- Summary row ----------
  const SummarySection = ({ label, value }: { label: string; value: string }) => (
    value ? (
      <div className="flex flex-col gap-0.5 py-1 text-sm">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="font-medium text-foreground truncate">{value}</span>
      </div>
    ) : null
  );

  // ---------- Render steps ----------
  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-5 animate-fade-in">
            <div className="space-y-2">
              <Label>{t("Applicant Type", "نوع المتقدم")} *</Label>
              <Select value={formData.applicantType} onValueChange={(v) => update('applicantType', v)}>
                <SelectTrigger className="h-11 rounded-lg border-border/70 bg-background hover:border-foreground/30 focus:border-primary transition-colors gap-2">
                  {formData.applicantType === 'agency' ? (
                    <Building2 className="h-4 w-4 text-primary shrink-0" />
                  ) : (
                    <User className="h-4 w-4 text-primary shrink-0" />
                  )}
                  <SelectValue placeholder={t("Select type", "اختر النوع")} />
                </SelectTrigger>
                <SelectContent className="rounded-lg border-border/60 shadow-lg p-1">
                  <SelectItem
                    value="individual"
                    className="py-2.5 rounded-md cursor-pointer focus:bg-primary/10 focus:text-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[state=checked]:font-medium"
                  >
                    {t("Individual", "فرد")}
                  </SelectItem>
                  <SelectItem
                    value="agency"
                    className="py-2.5 rounded-md cursor-pointer focus:bg-primary/10 focus:text-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[state=checked]:font-medium"
                  >
                    {t("Agency", "وكالة")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("Full Name", "الاسم الكامل")} *</Label>
              <Input className="h-11" value={formData.applicantName} onChange={(e) => update('applicantName', e.target.value)} placeholder={t("Enter your full name", "أدخل اسمك الكامل")} />
            </div>
            {isAgency && (
              <div className="space-y-2 animate-fade-in">
                <Label>{t("Company / Agency Name", "اسم الشركة / الوكالة")} *</Label>
                <Input className="h-11" value={formData.companyName} onChange={(e) => update('companyName', e.target.value)} placeholder={t("Enter company name", "أدخل اسم الشركة")} />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t("Email", "البريد الإلكتروني")} *</Label>
              <Input className="h-11" type="email" value={formData.email} onChange={(e) => update('email', e.target.value)} disabled={!!user?.email} />
            </div>
            <div className="space-y-2">
              <Label>{t("Phone Number", "رقم الهاتف")} *</Label>
              <Input className="h-11" value={formData.phone} onChange={(e) => update('phone', e.target.value)} placeholder={t("Enter phone number", "أدخل رقم الهاتف")} />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-5 animate-fade-in">
            {isAgency ? (
              <>
                <SectionHeader>{t("Person in Charge", "المسؤول")}</SectionHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("Name", "الاسم")} *</Label>
                    <Input className="h-11" value={formData.personInChargeName} onChange={(e) => update('personInChargeName', e.target.value)} placeholder={t("Full name", "الاسم الكامل")} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("Title / Position", "المسمى الوظيفي")} *</Label>
                    <Input className="h-11" value={formData.personInChargeTitle} onChange={(e) => update('personInChargeTitle', e.target.value)} placeholder={t("e.g. CEO, Manager", "مثل: مدير، رئيس تنفيذي")} />
                  </div>
                </div>

                <div className="border-t border-border/40 pt-4">
                  <SectionHeader>{t("Commercial Registration", "السجل التجاري")}</SectionHeader>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("CR Number", "رقم السجل التجاري")} *</Label>
                    <Input className="h-11" value={formData.crNumber} onChange={(e) => update('crNumber', e.target.value)} placeholder={t("Enter CR number", "أدخل رقم السجل التجاري")} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("VAT Number", "الرقم الضريبي")} *</Label>
                    <Input className="h-11" value={formData.vatNumber} onChange={(e) => update('vatNumber', e.target.value)} placeholder={t("Enter VAT number", "أدخل الرقم الضريبي")} />
                  </div>
                </div>
                <FileUploadField
                  label={t("Company Registration Document (PDF)", "وثيقة السجل التجاري (PDF)")}
                  file={companyRegFile}
                  onChange={(e) => handleFileChange(e, setCompanyRegFile)}
                />
                <FileUploadField
                  label={t("VAT Registration Document (PDF)", "وثيقة التسجيل الضريبي (PDF)")}
                  file={vatRegFile}
                  onChange={(e) => handleFileChange(e, setVatRegFile)}
                />
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>{t("Country", "الدولة")} *</Label>
                  <Select value={formData.country} onValueChange={(v) => update('country', v)}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={t("Select country", "اختر الدولة")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{isRTL ? c.ar : c.en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      {formData.country === "SA"
                        ? t("National ID or Iqama", "الهوية الوطنية أو الإقامة")
                        : t("National ID Number", "رقم الهوية الوطنية")} *
                    </Label>
                    <Input
                      className="h-11"
                      value={formData.nationalIdNumber}
                      onChange={(e) => update('nationalIdNumber', e.target.value)}
                      placeholder={formData.country === "SA"
                        ? t("Enter National ID or Iqama number", "أدخل رقم الهوية أو الإقامة")
                        : t("Enter National ID number", "أدخل رقم الهوية الوطنية")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("Date of Birth", "تاريخ الميلاد")} *</Label>
                    {(() => {
                      const maxYear = new Date().getFullYear() - 18;
                      const minYear = 1940;

                      const selDay = dobParts.d;
                      const selMonth = dobParts.m;
                      const selYear = dobParts.y;

                      const daysInMonth = (m?: number, y?: number) => {
                        if (m === undefined || y === undefined) return 31;
                        return new Date(y, m + 1, 0).getDate();
                      };

                      const commit = (d?: number, m?: number, y?: number) => {
                        let safeD = d;
                        if (d !== undefined && m !== undefined && y !== undefined) {
                          const maxD = daysInMonth(m, y);
                          safeD = Math.min(d, maxD);
                        }
                        setDobParts({ d: safeD, m, y });
                        if (safeD !== undefined && m !== undefined && y !== undefined) {
                          update('dateOfBirth', format(new Date(y, m, safeD), 'yyyy-MM-dd'));
                        } else {
                          update('dateOfBirth', '');
                        }
                      };

                      const years: number[] = [];
                      for (let y = maxYear; y >= minYear; y--) years.push(y);
                      const months = Array.from({ length: 12 }, (_, i) => ({
                        value: i,
                        label: format(new Date(2000, i, 1), "MMMM", { locale: isRTL ? ar : undefined }),
                      }));
                      const dayCount = daysInMonth(selMonth, selYear);
                      const days = Array.from({ length: dayCount }, (_, i) => i + 1);

                      return (
                        <div className="space-y-2">
                          <div className="grid grid-cols-3 max-[360px]:grid-cols-2 gap-2">
                            <Select
                              value={selDay !== undefined ? String(selDay) : undefined}
                              onValueChange={(v) => commit(Number(v), selMonth, selYear)}
                            >
                              <SelectTrigger className="h-11 w-full rounded-lg border-border/70 hover:border-foreground/30 focus:border-primary transition-colors [&>span]:text-center [&>span]:flex-1">
                                <SelectValue placeholder={t("Day", "اليوم")} />
                              </SelectTrigger>
                              <SelectContent className="rounded-lg border-border/60 shadow-lg p-1 max-h-72">
                                {days.map((d) => (
                                  <SelectItem
                                    key={d}
                                    value={String(d)}
                                    className="justify-center text-center py-2 rounded-md cursor-pointer focus:bg-primary/10 focus:text-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[state=checked]:font-medium"
                                  >
                                    {d}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select
                              value={selMonth !== undefined ? String(selMonth) : undefined}
                              onValueChange={(v) => commit(selDay, Number(v), selYear)}
                            >
                              <SelectTrigger className="h-11 w-full rounded-lg border-border/70 hover:border-foreground/30 focus:border-primary transition-colors [&>span]:text-center [&>span]:flex-1">
                                <SelectValue placeholder={t("Month", "الشهر")} />
                              </SelectTrigger>
                              <SelectContent className="rounded-lg border-border/60 shadow-lg p-1 max-h-72">
                                {months.map((m) => (
                                  <SelectItem
                                    key={m.value}
                                    value={String(m.value)}
                                    className="justify-center text-center py-2 rounded-md cursor-pointer focus:bg-primary/10 focus:text-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[state=checked]:font-medium"
                                  >
                                    {m.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select
                              value={selYear !== undefined ? String(selYear) : undefined}
                              onValueChange={(v) => commit(selDay, selMonth, Number(v))}
                            >
                              <SelectTrigger className="h-11 w-full max-[360px]:col-span-2 rounded-lg border-border/70 hover:border-foreground/30 focus:border-primary transition-colors [&>span]:text-center [&>span]:flex-1">
                                <SelectValue placeholder={t("Year", "السنة")} />
                              </SelectTrigger>
                              <SelectContent className="rounded-lg border-border/60 shadow-lg p-1 max-h-72">
                                {years.map((y) => (
                                  <SelectItem
                                    key={y}
                                    value={String(y)}
                                    className="justify-center text-center py-2 rounded-md cursor-pointer focus:bg-primary/10 focus:text-foreground data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[state=checked]:font-medium"
                                  >
                                    {y}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t("You must be 18 or older", "يجب أن يكون عمرك 18 سنة أو أكثر")}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <FileUploadField
                  label={formData.country === "SA"
                    ? t("National ID or Iqama Document (PDF)", "وثيقة الهوية الوطنية أو الإقامة (PDF)")
                    : t("National ID Document (PDF)", "وثيقة الهوية الوطنية (PDF)")}
                  file={nationalIdFile}
                  onChange={(e) => handleFileChange(e, setNationalIdFile)}
                />
              </>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-5 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("Bank Name", "اسم البنك")} *</Label>
                <Input className="h-11" value={formData.bankName} onChange={(e) => update('bankName', e.target.value)} placeholder={t("e.g. Al Rajhi Bank", "مثل: مصرف الراجحي")} />
              </div>
              <div className="space-y-2">
                <Label>{t("Account Holder Name", "اسم صاحب الحساب")} *</Label>
                <Input className="h-11" value={formData.accountHolderName} onChange={(e) => update('accountHolderName', e.target.value)} placeholder={t("Name as on bank account", "الاسم كما في الحساب البنكي")} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("IBAN", "رقم الآيبان")} *</Label>
              <Input className="h-11" value={formData.iban} onChange={(e) => update('iban', e.target.value)} placeholder={t("e.g. SA0380000000608010167519", "مثل: SA0380000000608010167519")} />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("City", "المدينة")} *</Label>
                <Input className="h-11" value={formData.city} onChange={(e) => update('city', e.target.value)} placeholder={t("e.g. Riyadh", "مثل: الرياض")} />
              </div>
              <div className="space-y-2">
                <Label>{t("District", "الحي")} *</Label>
                <Input className="h-11" value={formData.district} onChange={(e) => update('district', e.target.value)} placeholder={t("e.g. Al Olaya", "مثل: العليا")} />
              </div>
              <div className="space-y-2">
                <Label>{t("Street", "الشارع")} *</Label>
                <Input className="h-11" value={formData.street} onChange={(e) => update('street', e.target.value)} placeholder={t("Street name", "اسم الشارع")} />
              </div>
              <div className="space-y-2">
                <Label>{t("Postal Code", "الرمز البريدي")} *</Label>
                <Input className="h-11" value={formData.postalCode} onChange={(e) => update('postalCode', e.target.value)} placeholder={t("e.g. 12345", "مثل: 12345")} />
              </div>
              <div className="space-y-2">
                <Label>{t("Building Number", "رقم المبنى")} *</Label>
                <Input className="h-11" value={formData.buildingNumber} onChange={(e) => update('buildingNumber', e.target.value)} placeholder={t("e.g. 1234", "مثل: 1234")} />
              </div>
              <div className="space-y-2">
                <Label>{t("Additional Number", "الرقم الإضافي")} *</Label>
                <Input className="h-11" value={formData.additionalNumber} onChange={(e) => update('additionalNumber', e.target.value)} placeholder={t("e.g. 5678", "مثل: 5678")} />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-5 animate-fade-in">
            {/* Experience fields */}
            <div className="space-y-2">
              <Label>{t("Sales / Marketing Experience", "خبرة في المبيعات / التسويق")}</Label>
              <Textarea value={formData.experience} onChange={(e) => update('experience', e.target.value)} placeholder={t("Describe your experience", "اوصف خبرتك")} rows={3} className="resize-none" />
            </div>
            <div className="space-y-2">
              <Label>{t("Why do you want to join?", "لماذا تريد الانضمام؟")}</Label>
              <Textarea value={formData.reason} onChange={(e) => update('reason', e.target.value)} placeholder={t("Tell us your motivation", "أخبرنا عن دافعك")} rows={3} className="resize-none" />
            </div>
            <div className="space-y-2">
              <Label>{t("Website / Portfolio URL", "رابط الموقع / المحفظة")}</Label>
              <Input className="h-11" value={formData.websiteUrl} onChange={(e) => update('websiteUrl', e.target.value)} placeholder="https://" />
            </div>

            {/* Summary */}
            <div className="mt-4 rounded-xl border border-border/60 bg-card p-5 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-1 rounded-full bg-primary" />
                  <h4 className="text-sm font-semibold text-foreground">{t("Application Summary", "ملخص الطلب")}</h4>
                </div>
                <Badge variant="secondary">{isAgency ? t("Agency", "وكالة") : t("Individual", "فرد")}</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                <SummarySection label={t("Name", "الاسم")} value={formData.applicantName} />
                {isAgency && <SummarySection label={t("Company", "الشركة")} value={formData.companyName} />}
                <SummarySection label={t("Email", "البريد")} value={formData.email} />
                <SummarySection label={t("Phone", "الهاتف")} value={formData.phone} />
                <SummarySection label={t("Bank", "البنك")} value={formData.bankName} />
                <SummarySection label={t("IBAN", "آيبان")} value={formData.iban} />
                <SummarySection label={t("City", "المدينة")} value={formData.city} />
                {isAgency && <SummarySection label={t("CR #", "سجل تجاري")} value={formData.crNumber} />}
                {isAgency && <SummarySection label={t("VAT #", "ضريبي")} value={formData.vatNumber} />}
                {!isAgency && (
                  <SummarySection
                    label={formData.country === "SA"
                      ? t("National ID or Iqama", "الهوية الوطنية أو الإقامة")
                      : t("National ID", "هوية وطنية")}
                    value={formData.nationalIdNumber}
                  />
                )}
                <SummarySection label={t("Documents", "المستندات")} value={
                  isAgency
                    ? [companyRegFile?.name, vatRegFile?.name].filter(Boolean).join(', ')
                    : nationalIdFile?.name || ''
                } />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <AgreementStep
            isAgency={isAgency}
            data={{
              applicantName: formData.applicantName,
              companyName: formData.companyName,
              crNumber: formData.crNumber,
              vatNumber: formData.vatNumber,
              email: formData.email,
              personInChargeName: formData.personInChargeName,
              personInChargeTitle: formData.personInChargeTitle,
              nationalIdNumber: formData.nationalIdNumber,
              city: formData.city,
            }}
            onChange={setAgreement}
          />
        );

      default:
        return null;
    }
  };

  // ---------- Main render ----------
  return (
    <div
      className={cn("min-h-screen bg-muted/30", isRTL ? 'text-right' : 'text-left')}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => step === 0 ? navigate(-1) : goBack()}
          className="group mb-4 -ml-2 h-9 px-2 rounded-md gap-2 font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60"
        >
          <ArrowLeft
            className={cn(
              "h-4 w-4 transition-transform",
              isRTL ? "rotate-180 group-hover:-translate-x-0.5" : "group-hover:-translate-x-0.5"
            )}
          />
          {step === 0 ? t("Back", "رجوع") : t("Previous Step", "الخطوة السابقة")}
        </Button>

        {/* Hero */}
        <div className="mb-6 pb-5 border-b border-border/60">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
            {t("Referral Partner Application", "طلب شريك إحالة")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {t("Join our network and earn from every referral.", "انضم إلى شبكتنا واكسب من كل إحالة.")}
          </p>
        </div>

        <StepProgress />

        <Card className="border border-border/60 shadow-sm rounded-xl">
          <CardContent className="p-5 sm:p-7">
            <StepHeader />
            {renderStep()}

            {/* Navigation */}
            <div
              className={cn(
                "border-t border-border/60 pt-5 mt-6 gap-3",
                "flex flex-col-reverse sm:flex-row",
                step === 0 ? "sm:justify-end" : "sm:justify-between"
              )}
            >
              {step > 0 && (
                <Button
                  variant="outline"
                  onClick={goBack}
                  className="group h-11 rounded-lg gap-2 font-medium w-full sm:w-auto sm:min-w-[120px] border-border/70 hover:border-foreground/30 hover:bg-muted/60 text-foreground disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <ArrowLeft
                    className={cn(
                      "h-4 w-4 transition-transform",
                      isRTL ? "rotate-180 group-hover:-translate-x-0.5" : "group-hover:-translate-x-0.5"
                    )}
                  />
                  {t("Back", "رجوع")}
                </Button>
              )}
              {step < LAST_STEP ? (
                <Button
                  onClick={goNext}
                  className="group h-11 rounded-lg gap-2 font-medium w-full sm:w-auto sm:min-w-[140px] bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {t("Next", "التالي")}
                  <ArrowRight
                    className={cn(
                      "h-4 w-4 transition-transform",
                      isRTL ? "rotate-180 group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5"
                    )}
                  />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !agreement.accepted || (isAgency && (!agreement.signature || !agreement.stamp))}
                  className="h-11 rounded-lg gap-2 font-medium w-full sm:w-auto sm:min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="opacity-80">{t("Submitting…", "جارٍ الإرسال…")}</span>
                    </>
                  ) : (
                    <>
                      <Handshake className="h-4 w-4" />
                      {t("Submit Application", "إرسال الطلب")}
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AffiliateSignUp;
